class ProductRecommendations extends HTMLElement {
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};

  #activeFetch = null;

  #handleDesktopMediaChange = () => {
    this.#syncSingleDesktopVisibility();
  };

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#desktopMediaQuery.addEventListener('change', this.#handleDesktopMediaChange);
    requestAnimationFrame(() => this.#syncSingleDesktopVisibility());
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#desktopMediaQuery.removeEventListener('change', this.#handleDesktopMediaChange);
  }

  #syncSingleDesktopVisibility() {
    if (Shopify?.designMode) return;

    const slides = this.querySelectorAll('slideshow-slide.resource-list__slide');
    const gridItems = this.querySelectorAll('.resource-list__item');
    let count = slides.length;

    if (!count && gridItems.length) {
      count = gridItems.length;
    }

    if (!count) {
      const datasetCount = Number.parseInt(this.dataset.recommendationCount || '', 10);
      if (Number.isFinite(datasetCount) && datasetCount > 0) {
        count = datasetCount;
      }
    }

    const shouldHide = count <= 1 && count > 0;
    this.classList.toggle('product-recommendations--single-desktop', shouldHide);
  }

  #clampRecommendationLimit(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const rawLimit = Number.parseInt(parsed.searchParams.get('limit') || '10', 10);
      const limit = Number.isFinite(rawLimit) ? Math.min(Math.max(rawLimit, 1), 10) : 10;
      parsed.searchParams.set('limit', String(limit));
      return `${parsed.pathname}${parsed.search}`;
    } catch (_error) {
      return url.replace(/([?&])limit=\d+/i, '$1limit=10');
    }
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    if (this.#activeFetch) {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
        const hasProducts = Boolean(
          recommendations?.querySelector('slideshow-slide.resource-list__slide, .resource-list__item')
        );

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length && hasProducts) {
          this.dataset.recommendationsPerformed = 'true';
          this.classList.remove('hidden');
          this.innerHTML = recommendations.innerHTML;
          this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
            image.loading = 'eager';
          });
          requestAnimationFrame(() => {
            this.#syncSingleDesktopVisibility();
            document.dispatchEvent(
              new CustomEvent('shopify:section:load', {
                detail: { sectionId },
              })
            );
          });
          return;
        }

        this.dataset.recommendationsPerformed = 'true';
        if (!Shopify.designMode && !hasProducts) {
          this.classList.add('hidden');
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const baseUrl = this.#clampRecommendationLimit(this.dataset.url || '');
    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.dataset.recommendationsPerformed = 'true';
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
